// The contact form plugin from Great Southern Websites. Put in place by the
// website-builder elf as web/contact/contact-form.js and bundled by the web
// bundler into its own bundle (quarkus.web-bundler.bundle.contact=true);
// templates/partials/contact-form.html loads it on the contact page only.
// It renders in the light DOM so the site's own styles and theme tokens
// apply. Fields and the recipient come from the app at render time, so the
// owner changes them in Settings without a rebuild. Since app issue #159 the
// owner chooses the kind (contact, booking request, quote request) and the
// fields: text, long text, number, date, time, pick one and tick box. The
// app checks every answer again; the attributes here only help the visitor.
// Do not edit this file on a site: the elf replaces it with its current copy.
import { LitElement, html, nothing } from 'lit';

const FAILED = 'Sorry, the form could not be sent. Please try again in a moment.';
// A pick-one question with this many choices or fewer is shown as buttons; more is a dropdown.
const MAX_RADIOS = 4;

class GswContactForm extends LitElement {
  static properties = {
    site: { type: String },
    api: { type: String },
    _config: { state: true },
    _state: { state: true },
    _error: { state: true },
    _sent: { state: true },
  };

  createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    this._state = 'loading';
    this._load();
  }

  _url() {
    return String(this.api || '').replace(/\/+$/, '') + '/v1/public/contact/' + encodeURIComponent(this.site || '');
  }

  async _load() {
    try {
      const res = await fetch(this._url(), { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('unavailable');
      this._config = await res.json();
      this._state = 'ready';
    } catch (e) {
      this._state = 'unavailable';
    }
  }

  async _submit(e) {
    e.preventDefault();
    const body = {};
    for (const [k, v] of new FormData(e.target).entries()) body[k] = v;
    // An unticked box is not in FormData at all; say no rather than nothing.
    for (const f of this._config.fields) if (f.type === 'checkbox' && !(f.name in body)) body[f.name] = 'no';
    this._state = 'sending';
    this._error = '';
    try {
      const res = await fetch(this._url(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      let out = {};
      try { out = await res.json(); } catch (err) { /* no body */ }
      if (!res.ok) {
        this._error = out && out.error ? out.error : FAILED;
        this._state = 'ready';
        return;
      }
      this._sent = out && out.message ? out.message : 'Thank you, your message has been sent.';
      this._state = 'sent';
    } catch (err) {
      this._error = FAILED;
      this._state = 'ready';
    }
  }

  render() {
    if (this._state === 'loading') return html`<p class="gsw-contact-note">Loading the form...</p>`;
    if (this._state === 'unavailable') {
      return html`<p class="gsw-contact-note">The form is not available right now. Please use the phone number or email address on this page.</p>`;
    }
    if (this._state === 'sent') return html`<p class="gsw-contact-sent" role="status">${this._sent}</p>`;
    const sending = this._state === 'sending';
    return html`
      <form class="gsw-contact" @submit=${this._submit}>
        ${this._config.fields.map(f => this._field(f))}
        <div class="gsw-contact-trap" aria-hidden="true">
          <label>Leave this empty<input type="text" name="website" tabindex="-1" autocomplete="off"></label>
        </div>
        ${this._error ? html`<p class="gsw-contact-error" role="alert">${this._error}</p>` : nothing}
        <p><button class="btn btn-primary" type="submit" ?disabled=${sending}>${sending ? 'Sending...' : (this._config.button || 'Send message')}</button></p>
      </form>`;
  }

  _field(f) {
    const id = 'gsw-contact-' + f.name;
    const label = f.label + (f.required || f.type === 'checkbox' ? '' : ' (optional)');
    if (f.type === 'checkbox') {
      return html`<p class="gsw-contact-field gsw-contact-tick"><label><input id=${id} name=${f.name} type="checkbox" value="yes" ?required=${f.required}> ${f.label}</label></p>`;
    }
    if (f.type === 'select' && (f.options || []).length <= MAX_RADIOS) {
      return html`<fieldset class="gsw-contact-field gsw-contact-choice"><legend>${label}</legend>
        ${f.options.map((o, i) => html`<label><input type="radio" name=${f.name} value=${o} ?required=${f.required && i === 0}> ${o}</label>`)}
      </fieldset>`;
    }
    let control;
    if (f.type === 'select') {
      control = html`<select id=${id} name=${f.name} ?required=${f.required}>
        <option value="">Choose...</option>
        ${(f.options || []).map(o => html`<option value=${o}>${o}</option>`)}
      </select>`;
    } else if (f.type === 'textarea') {
      const max = f.name === 'message' ? (this._config.maxMessageChars || 5000) : 2000;
      control = html`<textarea id=${id} name=${f.name} rows=${f.name === 'message' ? 6 : 3} maxlength=${max} ?required=${f.required}></textarea>`;
    } else {
      const autocomplete = f.name === 'email' ? 'email' : f.name === 'phone' ? 'tel' : f.name === 'name' ? 'name' : 'off';
      control = html`<input id=${id} name=${f.name} type=${f.type} autocomplete=${autocomplete}
        min=${f.min ?? nothing} max=${f.max ?? nothing} step=${f.type === 'number' ? '1' : nothing}
        inputmode=${f.type === 'number' ? 'numeric' : nothing} ?required=${f.required}>`;
    }
    return html`<p class="gsw-contact-field"><label for=${id}>${label}</label>${control}</p>`;
  }
}

customElements.define('gsw-contact-form', GswContactForm);
